import { Modal } from "antd";
import i18n from "../../i18n";

type ReportLayoutMasterCsvErrorDetail = {
  rowNumber?: number;
  code?: string;
};

export type ReportLayoutMasterCsvErrorPayload = {
  errorCode?: string;
  errors?: ReportLayoutMasterCsvErrorDetail[];
};

const ERROR_MESSAGE_KEYS: Record<string, string> = {
  HEADER_MISSING: "reportLayout.csvErrors.codes.HEADER_MISSING",
  HEADER_INVALID: "reportLayout.csvErrors.codes.HEADER_INVALID",
  EMPTY_DATA: "reportLayout.csvErrors.codes.EMPTY_DATA",
  MISSING_REQUIRED_FIELDS: "reportLayout.csvErrors.codes.MISSING_REQUIRED_FIELDS",
  INVALID_CLOTHTYPE: "reportLayout.csvErrors.codes.INVALID_CLOTHTYPE",
  INVALID_LAYOUT_TYPE: "reportLayout.csvErrors.codes.INVALID_LAYOUT_TYPE",
  DUPLICATE_CLOTHTYPE: "reportLayout.csvErrors.codes.DUPLICATE_CLOTHTYPE",
  CSV_INVALID_RECORD_LENGTH: "reportLayout.csvErrors.codes.CSV_INVALID_RECORD_LENGTH",
  FILE_REQUIRED: "reportLayout.csvErrors.codes.FILE_REQUIRED",
  FILE_EXTENSION_INVALID: "reportLayout.csvErrors.codes.FILE_EXTENSION_INVALID",
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const toStringValue = (value: unknown): string | undefined =>
  typeof value === "string" ? value : undefined;

const toNumberValue = (value: unknown): number | undefined =>
  typeof value === "number" ? value : undefined;

const getErrorMessage = (code?: string): string | undefined =>
  code && ERROR_MESSAGE_KEYS[code] ? i18n.t(ERROR_MESSAGE_KEYS[code]) : undefined;

export const parseReportLayoutMasterCsvErrorPayload = async (
  response: Response
): Promise<ReportLayoutMasterCsvErrorPayload | undefined> => {
  try {
    const data = await response.clone().json();
    if (!isRecord(data)) return undefined;

    const errorCode = toStringValue(data.error_code);
    const errors = Array.isArray(data.errors)
      ? data.errors
        .filter(isRecord)
        .map((error) => ({
          rowNumber: toNumberValue(error.rowNumber),
          code: toStringValue(error.code),
        }))
      : undefined;

    if (!errorCode && (!errors || errors.length === 0)) {
      return undefined;
    }

    return { errorCode, errors };
  } catch (_error) {
    console.warn("採寸報告書レイアウトマスタCSVのエラーレスポンスを解析できませんでした。");
    return undefined;
  }
};

export const showReportLayoutMasterCsvErrorModal = (
  fileName: string,
  payload?: ReportLayoutMasterCsvErrorPayload
) => {
  const detailMessages = payload?.errors
    ?.map((error) => {
      const message = getErrorMessage(error.code);
      if (!message) return undefined;
      return error.rowNumber !== undefined
        ? i18n.t("reportLayout.csvErrors.row", { rowNumber: error.rowNumber, message })
        : message;
    })
    .filter((message): message is string => Boolean(message));

  const topLevelMessage = getErrorMessage(payload?.errorCode);
  const contentLines = detailMessages && detailMessages.length > 0
    ? detailMessages
    : [topLevelMessage ?? i18n.t("reportLayout.csvErrors.uploadFailed", { fileName })];

  Modal.error({
    centered: true,
    title: i18n.t("reportLayout.csvErrors.title"),
    content: (
      <div className="whitespace-pre-wrap">
        {contentLines.map((line, index) => <div key={`${line}-${index}`}>{line}</div>)}
      </div>
    ),
  });
};
